if (!customElements.get('ps-studio-navigation')) {
  customElements.define('ps-studio-navigation', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const listen = (element, event, handler) => element?.addEventListener(event, handler, { signal: this.controller.signal });
      this.disclosures = [...this.querySelectorAll('[data-nav-disclosure]')];
      this.backdrop = this.querySelector('[data-nav-dismiss]');
      this.mobile = this.querySelector('[data-mobile-dialog]');
      this.search = this.querySelector('[data-search-dialog]');
      this.desktop = window.matchMedia('(min-width: 990px)');
      const updateHeight = () => this.style.setProperty('--nav-height', `${this.querySelector('header').getBoundingClientRect().height}px`);
      this.resizeObserver = new ResizeObserver(updateHeight);
      this.resizeObserver.observe(this.querySelector('header'));
      updateHeight();
      this.disclosures.forEach((details) => {
        listen(details.querySelector('summary'), 'click', () => this.disclosures.forEach((other) => { if (other !== details) other.open = false; }));
        listen(details, 'toggle', () => { this.backdrop.hidden = !this.disclosures.some((item) => item.open); });
      });
      listen(this.backdrop, 'click', () => this.closeMenus());
      listen(document, 'click', (event) => { if (!this.contains(event.target)) this.closeMenus(); });
      listen(this, 'focusout', (event) => {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        clearTimeout(this.focusTimer);
        this.focusTimer = setTimeout(() => { if (!this.contains(document.activeElement)) this.closeMenus(); }, 0);
      });
      this.querySelectorAll('details').forEach((details) => {
        const summary = details.querySelector('summary');
        summary.setAttribute('role', 'button');
        summary.setAttribute('aria-expanded', String(details.open));
        listen(details, 'toggle', () => summary.setAttribute('aria-expanded', String(details.open)));
      });
      listen(this, 'keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (this.search.open || this.mobile.open) {
          event.preventDefault();
          event.stopPropagation();
          (this.search.open ? this.search : this.mobile).close();
        } else {
          const open = this.disclosures.find((details) => details.open);
          if (open) { event.preventDefault(); this.closeMenus(); open.querySelector('summary').focus(); }
        }
      });
      listen(document, 'keydown', (event) => {
        if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey || event.defaultPrevented) return;
        if (event.target.closest('input,textarea,select,[contenteditable="true"]') || document.querySelector('dialog[open]') || document.body.classList.contains('overflow-hidden')) return;
        event.preventDefault();
        this.openSearch(this.querySelector('.studio-nav__actions [data-search-open]'));
      });
      listen(this.querySelector('[data-mobile-open]'), 'click', (event) => {
        this.mobileTrigger = event.currentTarget;
        this.closeMenus();
        this.mobile.showModal();
        this.mobile.scrollTop = 0;
        document.body.classList.add('ps-nav-modal-open');
        this.querySelector('[data-mobile-close]').focus();
      });
      listen(this.querySelector('[data-mobile-close]'), 'click', () => this.mobile.close());
      listen(this.mobile, 'close', () => {
        if (!this.search.open) { document.body.classList.remove('ps-nav-modal-open'); this.mobileTrigger?.focus(); }
      });
      this.querySelectorAll('[data-search-open]').forEach((button) => listen(button, 'click', () => this.openSearch(button)));
      listen(this.querySelector('[data-search-close]'), 'click', () => this.search.close());
      listen(this.search, 'close', () => {
        document.body.classList.remove('ps-nav-modal-open');
        const searchElement = this.search.querySelector('predictive-search');
        searchElement?.close?.();
        this.searchTrigger?.focus();
      });
      listen(this.search, 'click', (event) => {
        if (event.target !== this.search) return;
        const rect = this.search.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.search.close();
      });
      this.breakpointChanged = () => {
        this.closeMenus();
        if (this.desktop.matches && this.mobile.open) this.mobile.close();
      };
      this.desktop.addEventListener('change', this.breakpointChanged);
      this.querySelectorAll('[data-brand-browser]').forEach((browser) => {
        const input = browser.querySelector('[data-brand-filter]');
        const items = [...browser.querySelectorAll('[data-brand-item]')];
        const normalize = (text) => text.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
        const update = () => {
          const terms = normalize(input.value).split(' ').filter(Boolean);
          let count = 0;
          items.forEach((item) => {
            const name = normalize(item.textContent);
            item.hidden = !terms.every((term) => name.includes(term));
            if (!item.hidden) count++;
          });
          browser.querySelectorAll('[data-brand-group]').forEach((group) => { group.hidden = ![...group.querySelectorAll('[data-brand-item]')].some((item) => !item.hidden); });
          browser.querySelector('[data-brand-status]').textContent = `${count} brand${count === 1 ? '' : 's'}${terms.length ? ' found' : ' to explore'}`;
          browser.querySelector('[data-brand-empty]').hidden = count > 0;
          browser.querySelector('[data-brand-clear]').hidden = !input.value;
        };
        listen(input, 'input', update);
        listen(browser.querySelector('[data-brand-clear]'), 'click', () => { input.value = ''; update(); input.focus(); });
        update();
      });
    }

    closeMenus() {
      this.disclosures.forEach((details) => { details.open = false; });
      this.backdrop.hidden = true;
    }

    openSearch(button) {
      this.searchTrigger = button.closest('[data-mobile-dialog]')
        ? this.querySelector('[data-mobile-open]')
        : button.closest('[data-nav-disclosure]')?.querySelector('summary') || button;
      this.closeMenus();
      if (this.mobile.open) this.mobile.close();
      this.search.showModal();
      document.body.classList.add('ps-nav-modal-open');
      this.search.querySelector('input[type="search"]').focus();
    }

    disconnectedCallback() {
      this.controller?.abort();
      clearTimeout(this.focusTimer);
      this.resizeObserver?.disconnect();
      this.desktop?.removeEventListener('change', this.breakpointChanged);
      document.body.classList.remove('ps-nav-modal-open');
    }
  });
}
